// ChatList.jsx — FINAL ENTERPRISE STABLE VERSION
//---------------------------------------------------

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useChat } from "../../hooks/useChat";
import { db } from "../../../../integrations/firebase";
import {
    ref,
    onValue,
    get,
    update,
    set as firebaseSet
} from "firebase/database";
import TeamList from "../../ui/Teams/TeamList";
import UserProfileEditor from "../../../user/ui/ProfileEditor/UserProfileEditor";
import CreateGroupModal from "../../ui/Groups/CreateGroupModal";
import {
    Search,
    Plus,
    X,
    Settings,
    Edit2,
    Users,
    MessageSquare,
    Check,
    CheckCheck,
    MoreHorizontal,
    Phone,
    Clock,
    Hash,
    AlertTriangle
} from "lucide-react";
import GroupCallModal from "../../ui/Call/GroupCallModal";
import CallHistoryModal from "../../ui/Call/CallHistoryModal";
import ChannelDirectoryModal, { ChannelGlyph } from "../Channels/ChannelDirectoryModal";
import { ChannelService } from "../../services/ChannelService";

const ChatList = ({
    onSelectChat = () => { },
    selectedChatId,
    currentUser: propCurrentUser
}) => {
    const { currentUser: contextUser, allUsers, isMockMode, startCall } = useChat();
    const currentUser = propCurrentUser || contextUser;
    const userStatus = currentUser?.status || 'online';

    // Normalize User ID
    const currentUserId = useMemo(
        () => currentUser?.uid || currentUser?.id,
        [currentUser]
    );

    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");
    const [showNewChatModal, setShowNewChatModal] = useState(false);
    const [showCallModal, setShowCallModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [userSearch, setUserSearch] = useState("");
    const [showChannels, setShowChannels] = useState(false);

    // Notices waiting for MY acknowledgement, across every channel I am in. Refreshed on
    // open, every five minutes, and whenever an acknowledgement or notice is posted here.
    const [pendingAcks, setPendingAcks] = useState([]);
    useEffect(() => {
        if (!currentUserId) return undefined;
        let live = true;
        const refresh = () => ChannelService.pendingAcks()
            .then(p => { if (live) setPendingAcks(p); })
            .catch(() => { /* the banner is a convenience; never break the list over it */ });
        // Listing channels first is what adds a newly joined staff member to the
        // company-wide channels (the server does it on that call), so their
        // "please acknowledge" items exist before we ask for them.
        ChannelService.list().catch(() => {}).finally(refresh);
        const timer = setInterval(refresh, 5 * 60 * 1000);
        window.addEventListener('channel-ack-changed', refresh);
        return () => { live = false; clearInterval(timer); window.removeEventListener('channel-ack-changed', refresh); };
    }, [currentUserId]);

    // Sidebar theme - read from localStorage and listen for changes
    const SIDEBAR_THEMES = {
        indigo: { bg: '#1e1b4b', text: '#e0e7ff', itemHover: 'rgba(255,255,255,0.10)', active: 'rgba(99,102,241,0.3)' },
        blue: { bg: '#0c4a6e', text: '#e0f2fe', itemHover: 'rgba(255,255,255,0.10)', active: 'rgba(59,130,246,0.3)' },
        rose: { bg: '#4c0519', text: '#fce4ec', itemHover: 'rgba(255,255,255,0.10)', active: 'rgba(244,63,94,0.3)' },
        emerald: { bg: '#064e3b', text: '#d1fae5', itemHover: 'rgba(255,255,255,0.10)', active: 'rgba(16,185,129,0.3)' },
        sunset: { bg: '#451a03', text: '#fef3c7', itemHover: 'rgba(255,255,255,0.10)', active: 'rgba(245,158,11,0.3)' },
        dark: { bg: '#0f0f23', text: '#c8cad0', itemHover: 'rgba(255,255,255,0.06)', active: 'rgba(99,102,241,0.3)' },
    };
    const [sidebarThemeKey, setSidebarThemeKey] = useState(() => {
        try { return localStorage.getItem('chatTheme') || 'indigo'; } catch { return 'indigo'; }
    });
    useEffect(() => {
        const handler = () => {
            try { setSidebarThemeKey(localStorage.getItem('chatTheme') || 'indigo'); } catch {}
        };
        window.addEventListener('chatThemeChanged', handler);
        return () => window.removeEventListener('chatThemeChanged', handler);
    }, []);
    const sidebarTheme = SIDEBAR_THEMES[sidebarThemeKey] || SIDEBAR_THEMES.indigo;



    // Stable ref for allUsers to prevent effect re-subscription on status changes
    const allUsersRef = React.useRef(allUsers);
    useEffect(() => {
        allUsersRef.current = allUsers;
    }, [allUsers]);

    //---------------------------------------------------
    // FETCH CHATS LISTENER
    //---------------------------------------------------
    useEffect(() => {
        if (!currentUserId) {
            setLoading(false);
            return;
        }

        const userChatsRef = ref(db, `users/${currentUserId}/chats`);

        const unsubscribe = onValue(userChatsRef, async (snapshot) => {
            if (!snapshot.exists()) {
                setChats([]);
                setLoading(false);
                return;
            }

            const data = snapshot.val();
            // data is { targetId: chatId, ... } for 1:1, or { groupId: groupId, ... } for groups
            const pairs = Object.entries(data);

            const loadedChats = await Promise.all(pairs.map(async ([targetId, chatId]) => {
                // Detect group chats: if the value equals the key, or the chatId doesn't contain '_' (not a 1:1 ID)
                const isGroupChat = targetId === chatId || (typeof chatId === 'string' && !chatId.includes('_'));

                if (isGroupChat) {
                    // LOAD GROUP METADATA from conversation
                    try {
                        // ⚠️ This used to `get(conversations/{chatId})` — the WHOLE node,
                        // every message in the group's history — to display a name and the
                        // last line. On a busy group that is the entire conversation
                        // downloaded on every list render, and RTDB bills egress. Read only
                        // the four children actually used, in parallel.
                        const [metaSnap, lastSnap, membersSnap, unreadSnap, kindSnap] = await Promise.all([
                            get(ref(db, `conversations/${chatId}/name`)),
                            get(ref(db, `conversations/${chatId}/lastMessage`)),
                            get(ref(db, `conversations/${chatId}/members`)),
                            get(ref(db, `conversations/${chatId}/unread/${currentUserId}`)),
                            // Only channels have a kind. Their members live in channels/{id}.
                            get(ref(db, `conversations/${chatId}/kind`)),
                        ]);
                        if (kindSnap.exists()) {
                            const lastMsg = lastSnap.exists() ? (lastSnap.val() || {}) : {};
                            return {
                                chatId,
                                targetUserId: chatId,
                                name: metaSnap.val() || 'Channel',
                                photo: null,
                                status: null,
                                type: 'channel',
                                kind: kindSnap.val(),
                                isChannel: true,
                                isGroup: true,
                                lastMessage: lastMsg.text || '',
                                timestamp: lastMsg.timestamp || 0,
                                unreadCount: unreadSnap.exists() ? (Number(unreadSnap.val()) || 0) : 0,
                                lastMessageSenderId: lastMsg.senderId || null,
                                lastMessageStatus: lastMsg.status || null,
                                memberCount: null,
                            };
                        }
                        // No name and no members: the conversation is gone, not merely quiet.
                        if (!metaSnap.exists() && !membersSnap.exists()) return null;
                        const convo = {
                            name: metaSnap.val(),
                            members: membersSnap.exists() ? membersSnap.val() : null,
                            photo: null,          // not used by this row; avoid a 5th read
                            createdAt: 0,
                        };

                        const lastMsg = lastSnap.exists() ? (lastSnap.val() || {}) : {};
                        const unreadCount = unreadSnap.exists() ? (Number(unreadSnap.val()) || 0) : 0;

                        return {
                            chatId,
                            targetUserId: chatId,
                            name: convo.name || 'Group Chat',
                            photo: convo.photo || null,
                            status: null,
                            type: 'group',
                            lastMessage: lastMsg.text || '',
                            timestamp: lastMsg.timestamp || convo.createdAt || 0,
                            unreadCount,
                            lastMessageSenderId: lastMsg.senderId || null,
                            lastMessageStatus: lastMsg.status || null,
                            isGroup: true,
                            memberCount: convo.members ? Object.keys(convo.members).length : 0
                        };
                    } catch (err) {
                        console.warn('[ChatList] Failed to load group:', chatId, err);
                        return null;
                    }
                }

                // 1:1 CHAT — resolve target user
                let targetUser = allUsersRef.current.find(u => u.id === targetId);

                // Fallback fetch if not in context
                if (!targetUser) {
                    try {
                        const uSnap = await get(ref(db, `users/${targetId}`));
                        if (uSnap.exists()) {
                            targetUser = { id: targetId, ...uSnap.val() };
                        }
                    } catch (e) {
                        console.warn("Failed to fetch user", targetId);
                    }
                }

                const name = targetUser?.name || targetUser?.email || "Unknown User";
                const photo = targetUser?.photo;
                const status = targetUser?.status;

                // Fetch Last Message & Unread
                let lastMessage = "";
                let timestamp = 0;
                let unreadCount = 0;
                let lastMessageSenderId = null;
                let lastMessageStatus = null;

                try {
                    const lastMsgRef = ref(db, `conversations/${chatId}/lastMessage`);
                    const lastMsgSnap = await get(lastMsgRef);
                    if (lastMsgSnap.exists()) {
                        const lm = lastMsgSnap.val();
                        lastMessage = lm.text || (lm.type === 'image' ? '📷 Image' : '📎 File');
                        timestamp = lm.timestamp;
                        lastMessageSenderId = lm.senderId;
                        lastMessageStatus = lm.status;
                    }

                    const unreadRef = ref(db, `conversations/${chatId}/unread/${currentUserId}`);
                    const unreadSnap = await get(unreadRef);
                    if (unreadSnap.exists()) unreadCount = unreadSnap.val();

                } catch (err) {
                    console.error("Error fetching chat details", chatId, err);
                }

                return {
                    chatId,
                    targetUserId: targetId,
                    name,
                    photo,
                    status,
                    lastMessage,
                    timestamp,
                    unreadCount,
                    lastMessageSenderId,
                    lastMessageStatus
                };
            }));

            // Sort by recent
            const validChats = loadedChats.filter(chat => {
                if (!chat) return false;
                // Group chats always valid
                if (chat.isGroup) return true;
                // Filter out Unknown Users (Deleted/Ghost)
                if (chat.name === "Unknown User") return false;
                // Filter out Inactive Agents
                const user = allUsersRef.current.find(u => u.id === chat.targetUserId);
                if (user && user.type === 'agent' && (user.status === 'inactive' || user.isDeleted)) return false;

                return true;
            }).sort((a, b) => b.timestamp - a.timestamp);
            setChats(validChats);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUserId]); // Only re-sub if currentUserId changes

    //---------------------------------------------------
    // Stable unique chat ID (alphabetical order)
    //---------------------------------------------------
    const getDeterministicChatId = useCallback((uid1, uid2) => {
        if (!uid1 || !uid2) return null;
        return [uid1, uid2].sort().join("_");
    }, []);

    const formatTime = (ts) => {
        if (!ts) return "";
        const date = new Date(ts);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        } else if (diffDays === 1) {
            return "Yesterday";
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }
    };


    //---------------------------------------------------
    // RENDER CHECK
    //---------------------------------------------------
    // Filter logic...
    //---------------------------------------------------
    // OPEN CHAT (normalized object)
    //---------------------------------------------------
    const openChat = (userObj) => {
        // CHANNEL — its own type, so the window knows to read channels/{id}
        if (userObj.type === 'channel' || userObj.isChannel === true) {
            const chatId = userObj.chatId;
            if (!chatId) return;
            onSelectChat({
                chatId, id: chatId, targetUserId: chatId,
                name: userObj.name, type: 'channel', isChannel: true, isGroup: true,
                kind: userObj.kind, photo: null, memberCount: userObj.memberCount,
                ...(userObj.focusMessageId ? { focusMessageId: userObj.focusMessageId } : {}),
            });
            setShowNewChatModal(false);
            return;
        }

        // GROUP CHAT — already has chatId, skip deterministic ID logic
        if (userObj.type === 'group' || userObj.isGroup === true) {
            const chatId = userObj.chatId;
            if (!chatId) return;

            onSelectChat({
                chatId,
                name: userObj.name,
                type: 'group',
                photo: userObj.photo,
                isGroup: true,
                memberCount: userObj.memberCount
            });

            // Reset unread count
            if (chatId && currentUserId) {
                firebaseSet(ref(db, `conversations/${chatId}/unread/${currentUserId}`), 0)
                    .catch(e => console.warn("Failed to reset unread count:", e));
            }
            setShowNewChatModal(false);
            return;
        }

        // 1:1 CHAT — standard flow
        const normalized = {
            id: userObj.id || userObj.targetUserId,
            targetUserId: userObj.id || userObj.targetUserId,
            name: userObj.name,
            email: userObj.email,
            mobile: userObj.mobile,
            type: userObj.type,
            role: userObj.role,
            status: userObj.status,
            photo: userObj.photo
        };

        const chatId = getDeterministicChatId(currentUserId, normalized.id);

        if (!chatId) return;

        // Sync chat lists ONLY if needed
        const existingChat = chats.find(c => c.targetUserId === normalized.id);
        if (!existingChat || existingChat.chatId !== chatId) {
            const updates = {};
            updates[`users/${currentUserId}/chats/${normalized.id}`] = chatId;
            updates[`users/${normalized.id}/chats/${currentUserId}`] = chatId;
            update(ref(db), updates).catch(e => console.error("Error syncing chat list:", e));
        }

        onSelectChat({
            chatId,
            ...normalized
        });

        // Reset unread count
        if (chatId && currentUserId) {
            firebaseSet(ref(db, `conversations/${chatId}/unread/${currentUserId}`), 0)
                .catch(e => console.warn("Failed to reset unread count:", e));
        }

        setShowNewChatModal(false);
    };

    const [chatFilter, setChatFilter] = useState('all');

    /** Case- and punctuation-insensitive, so "varn", "VARN" and "Varna " all match. */
    const needle = useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);
    const hay = (...vals) => vals.filter(Boolean).join(' ').toLowerCase();

    const filteredChats = useMemo(() => {
        // `c.name` was indexed directly — a chat with no name threw a TypeError here and
        // took the whole list down.
        let result = !needle ? chats : chats.filter((c) =>
            hay(c.name, c.lastMessage, c.targetUserId).includes(needle)
        );
        if (chatFilter === 'unread') result = result.filter(c => c.unreadCount > 0);
        if (chatFilter === 'groups') result = result.filter(c => c.isGroup && !c.isChannel);
        if (chatFilter === 'channels') result = result.filter(c => c.isChannel);
        if (chatFilter === 'favorites') result = result.filter(c => c.isFavorite);
        return result;
    }, [chats, needle, chatFilter]);

    /**
     * People you have NOT yet chatted with, matching the same search box.
     *
     * The main search only ever filtered existing conversations, so looking for a
     * colleague you had not messaged before returned "No conversations yet" — the thing
     * every messenger does, and the one this list could not do. Matches name, email and
     * mobile, because a name is often typed differently from how it is stored.
     */
    const peopleResults = useMemo(() => {
        if (!needle || chatFilter !== 'all') return [];
        const existing = new Set(chats.map(c => c.targetUserId));
        return (allUsers || [])
            .filter(u => u && u.id && u.id !== currentUserId && !existing.has(u.id))
            .filter(u => hay(u.name, u.email, u.mobile, u.role).includes(needle))
            .slice(0, 8);               // a search result list, not a directory dump
    }, [allUsers, chats, needle, chatFilter, currentUserId]);

    const filteredUsers = allUsers.filter(
        (u) =>
            u.id !== currentUserId &&
            u.name?.toLowerCase().includes(userSearch.toLowerCase())
    );

    // Theme-aware colors for sidebar — WHITE/LIGHT card design matching mockup
    const sColors = {
        cardBg: '#ffffff',
        headerBg: '#ffffff',
        searchBg: '#f1f3f5',
        searchBorder: '#e5e7eb',
        searchText: '#1e293b',
        searchPlaceholder: '#94a3b8',
        titleText: '#1e293b',
        tabBg: '#f1f3f5',
        tabActiveBg: '#6366f1',
        tabActiveText: '#ffffff',
        tabText: '#64748b',
        nameText: '#1e293b',
        msgText: '#94a3b8',
        timeText: '#94a3b8',
        itemBorder: '#f1f3f5',
        divider: '#f1f3f5',
        hoverBg: '#f8fafc',
        activeBg: '#eef2ff',
        activeBorder: '#6366f1',
    };

    return (
        <div className="w-full h-full flex flex-col border-r border-slate-200 bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
            {/* GRADIENT HEADER — Mockup Style */}
            <div className="indigo-violet-gradient px-6 pt-6 pb-8 text-white flex-shrink-0">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-xl font-bold tracking-tight">Messages</h1>
                    <div className="flex gap-1">
                        <button onClick={() => setShowChannels(true)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Channels" aria-label="Browse and create channels">
                            <Hash size={18} />
                        </button>
                        <button onClick={() => setShowGroupModal(true)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Create Group">
                            <Users size={18} />
                        </button>
                        <button onClick={() => setShowNewChatModal(true)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="New Chat">
                            <Edit2 size={16} />
                        </button>
                    </div>
                </div>

                {/* Profile Row */}
                <div className="flex items-center gap-2.5 mb-6 cursor-pointer hover:opacity-80 transition-opacity relative z-10" onClick={() => setIsProfileOpen(true)}>
                    <img
                        src={currentUser?.photo || `https://ui-avatars.com/api/?name=${currentUser?.name || 'User'}&background=6366f1&color=fff`}
                        className="w-8 h-8 rounded-full border-2 border-white/30 shadow-sm object-cover"
                        alt="Profile"
                    />
                    <div className="overflow-hidden flex-1">
                        <h2 className="text-xs font-bold truncate max-w-[120px] text-white">
                            {currentUser?.name || 'User'}
                        </h2>
                        <div className="flex items-center gap-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${userStatus === 'online' ? 'bg-emerald-400' : 'bg-white/40'}`}></div>
                            <span className="text-[10px] capitalize text-white/60">{userStatus}</span>
                        </div>
                    </div>
                    <div className="flex gap-0.5">
                        <button onClick={(e) => { e.stopPropagation(); setShowCallModal(true); }} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/60">
                            <Phone size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setShowHistoryModal(true); }} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/60">
                            <Clock size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setIsProfileOpen(true); }} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/60">
                            <Settings size={14} />
                        </button>
                    </div>
                </div>
                <UserProfileEditor
                    currentUser={currentUser || { name: 'Guest', uid: 'guest' }}
                    isOpen={isProfileOpen}
                    onClose={() => setIsProfileOpen(false)}
                />

                {/* Search — White on gradient */}
                <div className="relative mb-4 z-10">
                    <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70"
                        size={14}
                    />
                    <input
                        className="w-full rounded-2xl pl-9 pr-3 py-2.5 bg-white/10 border border-white/20 text-white placeholder-white/60 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/30 transition-all shadow-inner"
                        placeholder="Search chats..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Filter Tabs — INSIDE gradient */}
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 z-10 relative">
                    {[
                        { key: 'all', label: 'All' },
                        { key: 'unread', label: 'Unread' },
                        { key: 'groups', label: 'Groups' },
                        { key: 'channels', label: 'Channels' },
                        { key: 'favorites', label: 'Favorites' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setChatFilter(tab.key)}
                            className={chatFilter === tab.key
                                ? 'bg-white text-indigo-700 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap shadow-sm'
                                : 'bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border border-white/10'
                            }
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <ChannelDirectoryModal
                isOpen={showChannels}
                onClose={() => setShowChannels(false)}
                onOpenChannel={(c) => openChat(c)}
                allUsers={allUsers}
                currentUserId={currentUserId}
            />

            {/* Chat List Scroll Area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar bg-slate-50/50 p-4">
                {/* Notices waiting for my acknowledgement — an explicit action, never implied by reading */}
                {pendingAcks.length > 0 && (
                    <div className="mb-3 mx-1 rounded-2xl border border-amber-200 bg-amber-50 p-3" role="region" aria-label="Notices to acknowledge">
                        <p className="text-xs font-bold text-amber-800 mb-1.5">
                            {pendingAcks.length === 1 ? '1 notice needs' : `${pendingAcks.length} notices need`} your acknowledgement
                        </p>
                        {pendingAcks.slice(0, 4).map(p => (
                            <button
                                key={`${p.chatId}:${p.messageId}`}
                                onClick={() => openChat({ chatId: p.chatId, name: p.channelName, type: 'channel', isChannel: true, focusMessageId: p.messageId })}
                                className="w-full text-left rounded-xl px-2.5 py-1.5 hover:bg-amber-100/70"
                            >
                                <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
                                    #{p.channelName}
                                    {p.overdue && <span className="flex items-center gap-0.5 text-red-600"><AlertTriangle size={11} /> overdue</span>}
                                </span>
                                <span className="block text-[11px] text-slate-500 truncate">{p.excerpt}</span>
                            </button>
                        ))}
                    </div>
                )}
                {filteredChats.length === 0 && peopleResults.length === 0 ? (
                    <div className="p-8 text-center flex flex-col items-center mt-12 mx-4">
                        <div className="bg-indigo-500/10 p-5 rounded-2xl mb-4">
                            <MessageSquare className="text-indigo-400 animate-pulse" size={36} />
                        </div>
                        <span className="text-sm font-medium text-slate-700">
                            {needle ? `Nothing found for “${searchTerm.trim()}”`
                                : chatFilter === 'all' ? 'No conversations yet' : `No ${chatFilter} chats`}
                        </span>
                        <span className="text-xs mt-1 text-slate-400">
                            {needle ? 'Try a name, email or mobile number'
                                : chatFilter === 'all' ? 'Start a new chat to get going!' : 'Try a different filter'}
                        </span>
                    </div>
                ) : (
                    filteredChats.map((chat) => {
                        const isActive = selectedChatId === chat.chatId;
                        return (
                            <div
                                key={chat.chatId}
                                onClick={() => openChat(chat)}
                                className={`group relative flex items-center p-3 mb-3 mx-1 cursor-pointer transition-all duration-200 bg-white rounded-2xl ${isActive
                                        ? 'shadow-[0_4px_16px_-4px_rgba(99,102,241,0.2)] ring-1 ring-indigo-100 scale-[1.02]'
                                        : 'shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-slate-100'
                                    }`}
                            >
                                <div className="relative flex-shrink-0 mr-3">
                                    {chat.isChannel ? (
                                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center shadow-sm">
                                            <ChannelGlyph kind={chat.kind} size={20} />
                                        </div>
                                    ) : (
                                    <div className="w-12 h-12 rounded-full overflow-hidden border border-slate-100 shadow-sm">
                                        <img
                                            src={chat.photo || `https://ui-avatars.com/api/?name=${chat.name}&background=f1f5f9&color=64748b`}
                                            alt={chat.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${chat.name}&background=f1f5f9&color=64748b` }}
                                        />
                                    </div>
                                    )}
                                    {chat.status === 'online' && !chat.isGroup && (
                                        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white shadow-sm" />
                                    )}
                                    {chat.isGroup && !chat.isChannel && (
                                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-violet-500 rounded-full border-[1.5px] border-white flex items-center justify-center">
                                            <Users size={8} className="text-white" />
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="ml-3 flex-1 overflow-hidden">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <h3 className="text-sm font-semibold truncate text-slate-800">
                                                {chat.name}
                                            </h3>
                                            {chat.isGroup && !chat.isChannel && (
                                                <span className="text-[9px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0">
                                                    {chat.memberCount || 0}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-[10px] text-slate-400 flex-shrink-0">
                                            {formatTime(chat.timestamp)}
                                        </span>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <p className="text-xs text-slate-500 truncate max-w-[160px]">
                                            {chat.lastMessageSenderId === currentUserId && <span className="font-medium text-slate-600">You: </span>}
                                            {chat.lastMessage || "No messages"}
                                        </p>

                                        {/* Status/Unread */}
                                        {chat.lastMessageSenderId === currentUserId ? (
                                            <span className="text-slate-400">
                                                {chat.lastMessageStatus === "seen" ? <CheckCheck size={14} className="text-blue-400" /> : <Check size={14} />}
                                            </span>
                                        ) : (
                                            chat.unreadCount > 0 && (
                                                <div className="px-1.5 h-[18px] min-w-[18px] rounded-full bg-indigo-500 text-white flex items-center justify-center text-[9px] font-bold shadow-md shadow-indigo-500/30">
                                                    {chat.unreadCount}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}

                {/* PEOPLE — colleagues you have not messaged yet, so a search can reach
                    anyone in the business rather than only past conversations. */}
                {peopleResults.length > 0 && (
                    <div className="mt-2">
                        <div className="px-2 pb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                            Start a new chat
                        </div>
                        {peopleResults.map((u) => (
                            <div
                                key={u.id}
                                onClick={() => openChat({
                                    id: u.id, name: u.name, email: u.email, mobile: u.mobile,
                                    type: u.type, role: u.role, status: u.status,
                                    photo: u.photo || u.avatar || '',   // or the bubble shows initials
                                })}
                                className="flex items-center p-3 mb-2 mx-1 cursor-pointer bg-white rounded-2xl border border-dashed border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all duration-200"
                            >
                                <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 font-bold flex items-center justify-center border border-indigo-100 mr-3 flex-shrink-0">
                                    {(u.name || '?').charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-bold text-sm text-slate-800 truncate">{u.name || u.email || u.mobile}</p>
                                    <p className="text-xs text-slate-400 capitalize truncate">
                                        {[u.role, u.mobile].filter(Boolean).join(' · ') || 'No previous messages'}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* GROUP CALL MODAL */}
            <GroupCallModal
                isOpen={showCallModal}
                onClose={() => setShowCallModal(false)}
                currentUser={currentUser}
                onStartCall={(targetIds, type) => {
                    startCall(targetIds, type);
                    setShowCallModal(false);
                }}
            />

            {/* CALL HISTORY MODAL */}
            <CallHistoryModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
            />

            {/* NEW CHAT MODAL */}
            {showNewChatModal && (
                <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden ring-1 ring-slate-200/50 border border-slate-100 flex flex-col max-h-[500px] animate-in zoom-in-95 duration-200">
                        <div className="p-4 border-b flex justify-between items-center bg-indigo-50">
                            <h3 className="font-bold text-indigo-900">New Chat</h3>
                            <button onClick={() => setShowNewChatModal(false)} className="p-1 hover:bg-white/50 rounded-full transition-colors">
                                <X size={20} className="text-indigo-500" />
                            </button>
                        </div>

                        <div className="p-4">
                            <input
                                placeholder="Search users by name..."
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                autoFocus
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto p-2">
                            {filteredUsers.length === 0 ? (
                                <div className="text-center p-4 text-slate-400 text-sm">No users found</div>
                            ) : (
                                filteredUsers.map((u) => (
                                    <div
                                        key={u.id}
                                        onClick={() =>
                                            openChat({
                                                id: u.id,
                                                name: u.name,
                                                email: u.email,
                                                mobile: u.mobile,
                                                type: u.type,
                                                role: u.role,
                                                status: u.status
                                            })
                                        }
                                        className="p-3 flex gap-3 hover:bg-indigo-50 cursor-pointer rounded-xl transition-all duration-200 items-center hover:scale-[1.01]"
                                    >
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold border border-indigo-200">
                                            {u.name?.charAt(0).toUpperCase()}
                                        </div>

                                        <div>
                                            <p className="font-bold text-sm text-slate-800">{u.name}</p>
                                            <p className="text-xs text-slate-500 capitalize">{u.role}</p>
                                        </div>
                                    </div>
                                )))}
                        </div>
                    </div>
                </div>
            )}

            {/* CREATE GROUP MODAL */}
            <CreateGroupModal
                currentUser={currentUser}
                isOpen={showGroupModal}
                onClose={() => setShowGroupModal(false)}
                onGroupCreated={(chatId) => {
                    setShowGroupModal(false);
                    // Open the newly created group chat
                    onSelectChat({
                        chatId,
                        name: 'New Group',
                        type: 'group'
                    });
                }}
            />
        </div>
    );
};

export default ChatList;
